import { useState, useCallback, useMemo, useRef, useEffect } from 'react'

export type SortDirection = 'asc' | 'desc' | null

export interface TableSortState {
  columnKey: string | null
  direction: SortDirection
}

export interface TableSettings {
  sort: TableSortState
  visibleColumns: Record<string, boolean>
  filters: Record<string, string | string[]>
}

const getStorageKey = (tableId: string, type: 'settings') => `table-${type}-${tableId}`

const loadSettings = (tableId: string): Partial<TableSettings> => {
  try {
    const item = window.localStorage.getItem(getStorageKey(tableId, 'settings'))
    if (item) {
      return JSON.parse(item)
    }
  } catch (error) {
    console.warn(`Error reading table settings for "${tableId}":`, error)
  }
  return {}
}

const saveSettings = (tableId: string, settings: TableSettings): void => {
  try {
    window.localStorage.setItem(getStorageKey(tableId, 'settings'), JSON.stringify(settings))
  } catch (error) {
    console.warn(`Error saving table settings for "${tableId}":`, error)
  }
}

export interface UseTableSettingsOptions {
  tableId: string
  defaultVisibleColumns?: Record<string, boolean>
  defaultSort?: TableSortState
}

export interface UseTableSettingsReturn {
  // Sort state
  sortColumn: string | null
  sortDirection: SortDirection
  toggleSort: (columnKey: string) => void
  setSort: (columnKey: string | null, direction: SortDirection) => void
  
  // Column visibility
  isColumnVisible: (columnKey: string) => boolean
  toggleColumnVisibility: (columnKey: string) => void
  setColumnVisibility: (columnKey: string, visible: boolean) => void
  
  // Column filters (text)
  getColumnFilter: (columnKey: string) => string
  setColumnFilter: (columnKey: string, value: string) => void
  // Column filters (array/enum)
  getColumnFilterArray: (columnKey: string) => string[]
  setColumnFilterArray: (columnKey: string, value: string[]) => void
  toggleColumnFilterArrayItem: (columnKey: string, item: string) => void
  // Common
  clearColumnFilter: (columnKey: string) => void
  clearAllSettings: () => void
  hasActiveSettings: () => boolean
  
  // Utility
  getSortState: (columnKey: string) => SortDirection
}

/**
 * Custom hook for managing table column sorting and visibility with localStorage persistence
 * @param options - Configuration options for the hook
 * @returns Object with sort and visibility management functions
 */
export const useTableSettings = (options: UseTableSettingsOptions): UseTableSettingsReturn => {
  const { tableId, defaultVisibleColumns = {}, defaultSort = { columnKey: null, direction: null } } = options

  // Load initial state from localStorage
  const loadedSettings = useMemo(() => loadSettings(tableId), [tableId])
  
  const [sortState, setSortState] = useState<TableSortState>(() => 
    loadedSettings.sort || defaultSort
  )
  
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(() => {
    // Merge defaults with loaded settings, with loaded settings taking precedence
    return { ...defaultVisibleColumns, ...loadedSettings.visibleColumns }
  })

  const [filters, setFilters] = useState<Record<string, string | string[]>>(() => 
    loadedSettings.filters || {}
  )

  // Track latest filters via ref to avoid stale closures
  const filtersRef = useRef(filters)
  useEffect(() => {
    filtersRef.current = filters
  }, [filters])

  // Save to localStorage whenever state changes
  const updateSettings = useCallback((sort: TableSortState, columns: Record<string, boolean>, filterValues: Record<string, string | string[]>) => {
    saveSettings(tableId, { sort, visibleColumns: columns, filters: filterValues })
  }, [tableId])

  const toggleSort = useCallback((columnKey: string) => {
    setSortState(prev => {
      let newState: TableSortState
      
      if (prev.columnKey === columnKey) {
        // Same column: flip the direction
        if (prev.direction === 'asc') {
          newState = { columnKey, direction: 'desc' }
        } else if (prev.direction === 'desc') {
          newState = { columnKey, direction: 'asc' }
        } else {
          // null -> start with asc
          newState = { columnKey, direction: 'asc' }
        }
      } else {
        // Different column: start with asc
        newState = { columnKey, direction: 'asc' }
      }
      
      setVisibleColumns(prevCols => {
        updateSettings(newState, prevCols, filtersRef.current)
        return prevCols
      })
      
      return newState
    })
  }, [updateSettings])

  const setSort = useCallback((columnKey: string | null, direction: SortDirection) => {
    setSortState(prev => {
      const newState: TableSortState = { columnKey, direction }
      setVisibleColumns(prevCols => {
        updateSettings(newState, prevCols, filtersRef.current)
        return prevCols
      })
      return newState
    })
  }, [updateSettings])

  const isColumnVisible = useCallback((columnKey: string): boolean => {
    // If not in the record, default to visible
    return visibleColumns[columnKey] !== false
  }, [visibleColumns])

  const toggleColumnVisibility = useCallback((columnKey: string) => {
    setVisibleColumns(prev => {
      const newCols = { ...prev, [columnKey]: !isColumnVisible(columnKey) }
      updateSettings(sortState, newCols, filtersRef.current)
      return newCols
    })
  }, [isColumnVisible, sortState, updateSettings])

  const setColumnVisibility = useCallback((columnKey: string, visible: boolean) => {
    setVisibleColumns(prev => {
      const newCols = { ...prev, [columnKey]: visible }
      updateSettings(sortState, newCols, filtersRef.current)
      return newCols
    })
  }, [sortState, updateSettings])

  const getColumnFilter = useCallback((columnKey: string): string => {
    const filter = filters[columnKey]
    return typeof filter === 'string' ? filter : ''
  }, [filters])

  const getColumnFilterArray = useCallback((columnKey: string): string[] => {
    const filter = filters[columnKey]
    return Array.isArray(filter) ? filter : []
  }, [filters])

  const setColumnFilter = useCallback((columnKey: string, value: string) => {
    setFilters(prev => {
      const newFilters = { ...prev, [columnKey]: value }
      updateSettings(sortState, visibleColumns, newFilters)
      return newFilters
    })
  }, [sortState, visibleColumns, updateSettings])

  const setColumnFilterArray = useCallback((columnKey: string, value: string[]) => {
    setFilters(prev => {
      const newFilters = { ...prev }
      if (value.length > 0) {
        newFilters[columnKey] = value
      } else {
        delete newFilters[columnKey]
      }
      updateSettings(sortState, visibleColumns, newFilters)
      return newFilters
    })
  }, [sortState, visibleColumns, updateSettings])

  const toggleColumnFilterArrayItem = useCallback((columnKey: string, item: string) => {
    setFilters(prev => {
      const current = Array.isArray(prev[columnKey]) ? prev[columnKey] as string[] : []
      const newArray = current.includes(item)
        ? current.filter(v => v !== item)
        : [...current, item]
      const newFilters = { ...prev }
      if (newArray.length > 0) {
        newFilters[columnKey] = newArray
      } else {
        delete newFilters[columnKey]
      }
      updateSettings(sortState, visibleColumns, newFilters)
      return newFilters
    })
  }, [sortState, visibleColumns, updateSettings])

  const clearColumnFilter = useCallback((columnKey: string) => {
    setFilters(prev => {
      const newFilters = { ...prev }
      delete newFilters[columnKey]
      updateSettings(sortState, visibleColumns, newFilters)
      return newFilters
    })
  }, [sortState, visibleColumns, updateSettings])

  const getSortIcon = useCallback((columnKey: string): SortDirection => {
    if (sortState.columnKey === columnKey) {
      return sortState.direction
    }
    return null
  }, [sortState])

  const clearAllSettings = useCallback(() => {
    const resetSort = defaultSort
    const resetColumns = { ...defaultVisibleColumns }
    const resetFilters: Record<string, string | string[]> = {}
    
    setSortState(resetSort)
    setVisibleColumns(resetColumns)
    setFilters(resetFilters)
    updateSettings(resetSort, resetColumns, resetFilters)
  }, [defaultSort, defaultVisibleColumns, updateSettings])

  const hasActiveSettings = useCallback(() => {
    // Check if there's an active sort
    if (sortState.columnKey !== null) {
      return true
    }

    // Check if there are any active filters
    if (Object.keys(filters).length > 0) {
      return true
    }

    // Check if any columns are hidden (different from defaults)
    for (const key in defaultVisibleColumns) {
      if (visibleColumns[key] !== defaultVisibleColumns[key]) {
        return true
      }
    }
    // Also check for columns that might be in visibleColumns but not in defaults
    for (const key in visibleColumns) {
      if (defaultVisibleColumns[key] === undefined && visibleColumns[key] === false) {
        return true
      }
    }

    return false
  }, [sortState.columnKey, filters, visibleColumns, defaultVisibleColumns])

  return {
    sortColumn: sortState.columnKey,
    sortDirection: sortState.direction,
    toggleSort,
    setSort,
    isColumnVisible,
    toggleColumnVisibility,
    setColumnVisibility,
    getColumnFilter,
    setColumnFilter,
    getColumnFilterArray,
    setColumnFilterArray,
    toggleColumnFilterArrayItem,
    clearColumnFilter,
    clearAllSettings,
    hasActiveSettings,
    getSortState: getSortIcon,
  }
}